// Dashboard (ADMIN_UI §3.2). Top-line cards + 30-day send chart + top
// campaigns by claim rate. Scoped by the app-dropdown. Polls every 30 s
// while visible.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AsyncState } from '../../components/AsyncState.js';
import { SendsChart } from '../../components/dashboard/SendsChart.js';
import { Badge } from '../../components/ui/badge.js';
import { Card, CardHeader, CardTitle } from '../../components/ui/card.js';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table.js';
import { getAnalyticsOverview, type RateBlock } from '../../lib/api/analytics.js';
import { useAppScope } from '../../lib/app-scope.js';

export const Route = createFileRoute('/_authed/dashboard')({
  component: DashboardPage,
});

function pct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function DashboardPage() {
  const scope = useAppScope((s) => s.app);
  const appFilter = scope === 'all' ? undefined : scope;
  const query = useQuery({
    queryKey: ['analytics', 'overview', appFilter],
    queryFn: () => getAnalyticsOverview(appFilter),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold text-neutral-900">Dashboard</h2>
      <AsyncState
        query={query}
        isEmpty={(d) =>
          d.sends_today === 0 &&
          d.last_7d.sends === 0 &&
          d.last_30d_send_series.every((p) => p.sends === 0)
        }
        empty={
          <p className="text-sm text-neutral-500">
            No sends yet. Once campaigns are active, this dashboard will populate.
          </p>
        }
      >
        {(data) => (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <StatCard label="Sends today" value={data.sends_today.toLocaleString()} />
              <RateCard
                label="Open rate (7d)"
                rate={data.last_7d.open_rate}
                block={data.last_7d}
                kind="opens"
              />
              <RateCard
                label="Click rate (7d)"
                rate={data.last_7d.click_rate}
                block={data.last_7d}
                kind="clicks"
              />
              <RateCard
                label="Claim rate (7d)"
                rate={data.last_7d.claim_rate}
                block={data.last_7d}
                kind="claims"
              />
              <RateCard
                label="Suppression rate (7d)"
                rate={data.last_7d.suppression_rate}
                block={data.last_7d}
                kind="suppressions"
                emphasis="bad"
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Sends — last 30 days</CardTitle>
              </CardHeader>
              <SendsChart data={data.last_30d_send_series} />
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top campaigns by claim rate (7d)</CardTitle>
              </CardHeader>
              {data.top_campaigns.length === 0 ? (
                <p className="text-sm text-neutral-500">
                  No campaigns with sends in the last 7 days.
                </p>
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>Campaign</TH>
                      <TH>App</TH>
                      <TH>Sends</TH>
                      <TH>Claims</TH>
                      <TH>Claim rate</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {data.top_campaigns.map((c) => (
                      <TR key={c.campaign_id}>
                        <TD>{c.name}</TD>
                        <TD>{c.app ?? 'all'}</TD>
                        <TD>{c.sends.toLocaleString()}</TD>
                        <TD>{c.claims.toLocaleString()}</TD>
                        <TD>
                          <Badge variant="success">{pct(c.claim_rate)}</Badge>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
            </Card>
          </div>
        )}
      </AsyncState>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="text-xs uppercase tracking-wide text-neutral-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-neutral-900">{value}</div>
    </div>
  );
}

function RateCard({
  label,
  rate,
  block,
  kind,
  emphasis,
}: {
  label: string;
  rate: number;
  block: RateBlock;
  kind: 'opens' | 'clicks' | 'claims' | 'suppressions';
  emphasis?: 'bad';
}) {
  const numerator = block[kind];
  const colour =
    emphasis === 'bad' ? (rate > 0.005 ? 'text-red-600' : 'text-neutral-900') : 'text-neutral-900';
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="text-xs uppercase tracking-wide text-neutral-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${colour}`}>{pct(rate)}</div>
      <div className="mt-1 text-xs text-neutral-500">
        {numerator.toLocaleString()} / {block.sends.toLocaleString()} sent
      </div>
    </div>
  );
}
